// jrs-v2-add-manifest.js — D-0 review addition (bounded add-manifest attribute).
//
// Purpose: close the vanished-line EVIDENCE gap that review item 2 opened. R2
// removed the debug log from cart ATTRIBUTES and kept only the per-LINE property
// write — but the #61567 precedent showed that when a line itself vanishes, its
// per-line log dies with it and an order-level attribute was the ONLY surviving
// witness. This module re-adds a *bounded* order-level witness: a single cart
// attribute (`_jrs_add_manifest`) holding a ring buffer of the last few adds with
// only compact fields (instance id / parent key, timestamp, the expected child
// `_lineItemKey`s + qtys, and the verification verdict). It is NOT the verbose
// debug log — it is the minimal "what did this add intend, and did it verify"
// record, written UNCONDITIONALLY (not gated on JRS_DEBUG_PROPERTIES_IN_CART) so
// it exists precisely when the debug log is off or has died.
//
// Hard caps (review): last MAX_ENTRIES adds, total serialized <= MAX_BYTES (~4KB),
// drop-oldest to fit. Small by construction (4KB) so the unload write fits the
// sendBeacon size limit (record() sends via sendBeacon first, fetch-keepalive fallback).
//
// The ring/cap logic lives in build() — a PURE function — so it is unit-tested
// directly (see __tests__/d0-add-manifest.test.js), not mirrored.
//
// ES5 / IIFE / window.JRS, consistent with the rest of the v2 stack.
(function () {
  window.JRS = window.JRS || {};
  if (window.JRS.AddManifest) { return; }

  var ATTR_KEY = '_jrs_add_manifest';
  var MAX_ENTRIES = 5;          // ring: last 5 adds
  var MAX_BYTES = 4096;         // ~4KB hard cap on the serialized attribute value

  // UTF-8 byte length without TextEncoder (jsdom + old WebKit safe).
  function byteLen(s) {
    s = String(s == null ? '' : s);
    var n = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c < 0x80) { n += 1; }
      else if (c < 0x800) { n += 2; }
      else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; } // surrogate pair => 4 bytes
      else { n += 3; }
    }
    return n;
  }

  function parseExisting(raw) {
    if (!raw) { return []; }
    if (Object.prototype.toString.call(raw) === '[object Array]') { return raw.slice(); }
    try {
      var arr = JSON.parse(raw);
      return (Object.prototype.toString.call(arr) === '[object Array]') ? arr : [];
    } catch (e) {
      return []; // malformed prior value => start clean rather than throw
    }
  }

  // Normalize one add into the compact stored shape. Only the fields a witness
  // needs; everything else is deliberately dropped to stay under the byte cap.
  //   t   - ISO timestamp
  //   id  - bundle_instance_id (bundle) | _ParentKey (customizer)
  //   fl  - flow: 'bundle' | 'customizer'
  //   v   - verification verdict
  //   exp - { "<_lineItemKey>": qty, ... } expected children
  function entry(o) {
    o = o || {};
    var e = {
      t: o.timestamp || '',
      id: String(o.id || ''),
      fl: o.flow || '',
      v: o.verdict || 'unverified'
    };
    if (o.expected && typeof o.expected === 'object') { e.exp = o.expected; }
    return e;
  }

  // PURE. Append `newEntry` to the parsed `existingRaw` ring, then enforce both
  // caps with drop-oldest. Returns { json, entries, dropped, bytes }.
  function build(existingRaw, newEntry, opts) {
    opts = opts || {};
    var maxEntries = opts.maxEntries != null ? opts.maxEntries : MAX_ENTRIES;
    var maxBytes = opts.maxBytes != null ? opts.maxBytes : MAX_BYTES;

    var entries = parseExisting(existingRaw);
    // ERP-4 hygiene, revised 2026-08-04: prune by CART MEMBERSHIP, not age.
    // B2B carts legitimately live for days (configure Monday, confirm sizes and
    // buy Tuesday) and the manifest is the order-level vanished-line witness —
    // an age prune would drop entries for lines STILL IN THE CART on the next
    // add. Instead an old entry survives iff it still references something in
    // the live cart (an exp child _lineItemKey, the bundle instance id, or a
    // parent variant id). Entries from cleared/abandoned carts — the original
    // staleness complaint — reference nothing and drop. Fails OPEN: when the
    // caller can't supply cart lines, nothing is pruned (the 5-entry /
    // byte caps below still bound the ring).
    if (opts.cartLines && opts.cartLines.length >= 0) {
      var liveKeys = {};
      var liveIds = {};
      for (var cl = 0; cl < opts.cartLines.length; cl++) {
        var line = opts.cartLines[cl] || {};
        var lp = line.properties || {};
        if (lp._lineItemKey) liveKeys[String(lp._lineItemKey)] = true;
        if (lp._bundle_instance_id) liveIds[String(lp._bundle_instance_id)] = true;
        if (line.variant_id) liveIds[String(line.variant_id)] = true;
      }
      entries = entries.filter(function (e) {
        if (!e) return false;
        if (e.id && liveIds[String(e.id)]) return true;
        if (e.exp) {
          for (var k in e.exp) {
            if (Object.prototype.hasOwnProperty.call(e.exp, k) && liveKeys[k]) return true;
          }
        }
        return false;
      });
    }
    if (newEntry) { entries.push(newEntry); }

    var dropped = 0;

    // 1) entry-count cap: keep the newest maxEntries.
    while (entries.length > maxEntries) { entries.shift(); dropped++; }

    // 2) byte cap: drop oldest until under, but always keep at least the newest.
    var json = JSON.stringify(entries);
    while (byteLen(json) > maxBytes && entries.length > 1) {
      entries.shift(); dropped++;
      json = JSON.stringify(entries);
    }

    // 3) degenerate case: a single newest entry still over cap — trim its
    //    expected map (keys are the bulky part) so the witness still fits.
    if (byteLen(json) > maxBytes && entries.length === 1 && entries[0] && entries[0].exp) {
      var only = entries[0];
      var keys = Object.keys(only.exp);
      while (keys.length > 0 && byteLen(JSON.stringify(entries)) > maxBytes) {
        delete only.exp[keys.pop()];
        only.expTrunc = true;
      }
      json = JSON.stringify(entries);
    }

    return { json: json, entries: entries, dropped: dropped, bytes: byteLen(json) };
  }

  // Build from the current attribute value + persist the bounded ring as a single cart
  // attribute. Transport: the shared JRS.beaconPost (jrs-v2-beacon-post.js) — sendBeacon
  // FIRST, the only write that survives the same-tick redirect on WebKit/Safari (where
  // fetch(keepalive) never initiates: no request, no error — the "witness never records"
  // gate-(a) failure). One unified transport with the bundle/customizer property writes.
  // Falls back to fetch(keepalive) if the helper isn't loaded / no sendBeacon. Caller
  // passes existingRaw (the current `_jrs_add_manifest` value from the cart it has in
  // hand — no read to abort). Best-effort: never throws, never blocks the redirect.
  // Always returns a Promise (true | fetch-result | null).
  function record(o) {
    o = o || {};
    var built = build(o.existingRaw, entry(o), o);
    var attrs = {};
    attrs[ATTR_KEY] = built.json;
    var url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root + 'cart/update.js'
      : '/cart/update.js';
    var bodyStr = JSON.stringify({ attributes: attrs });

    // 1) Shared sendBeacon-first transport (survives the same-tick redirect on Safari/WebKit).
    if (window.JRS && window.JRS.beaconPost && window.JRS.beaconPost(url, bodyStr)) {
      return Promise.resolve(true);
    }
    // 2) Fallback: fetch keepalive (engines without sendBeacon / helper not loaded).
    try {
      if (typeof fetch === 'function') {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: bodyStr,
          keepalive: true
        }).catch(function () { return null; });
      }
    } catch (e2) { /* nothing else to try */ }
    return Promise.resolve(null);
  }

  window.JRS.AddManifest = {
    build: build,
    entry: entry,
    record: record,
    byteLen: byteLen,
    ATTR_KEY: ATTR_KEY,
    MAX_ENTRIES: MAX_ENTRIES,
    MAX_BYTES: MAX_BYTES
  };
})();
